// nono-provetime.js: the page-specific island of 02 Prøvetime (FERDIG 02 §16). The §01 hero carousel wraps
// natively (<nono-carousel loop>, assets/nono-carousel.js), so it needs nothing from here.
//   <nono-slideshow>                §16 image loop (D-104): cross-fade, 3.1 s still + 0.9 s fade, 5 → 1, only
//                                   while visible and the tab is shown; sticky pause (WCAG 2.2.2); reduced motion,
//                                   Save-Data or no JS = image 1 only (images 2–5 stay in <template>s, unloaded).
// ES module, no dependencies, never auto-advances the hero.

const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches || !!(navigator.connection && navigator.connection.saveData);

class NonoSlideshow extends HTMLElement {
  connectedCallback() {
    if (this.btn !== undefined) return;
    this.btn = this.querySelector('[data-slideshow-toggle]');
    this.still = Number(this.dataset.still) || 3100;
    this.fade = Number(this.dataset.fade) || 900;
    this.style.setProperty('--p-pta-fade', `${this.fade}ms`);
    this.userPaused = false;
    this.visible = false;
    this.index = 0;
    if (!this.btn || reduced()) return; // image 1 only
    this.btn.hidden = false;
    this.btn.addEventListener('click', () => {
      this.userPaused = this.dataset.state === 'playing';
      if (this.userPaused) this.stop(); else this.start();
    });
    this.io = new IntersectionObserver((e) => {
      this.visible = e[0].isIntersecting;
      if (this.visible && !this.userPaused) this.start(); else this.stop(true);
    }, { threshold: 0.25 });
    this.io.observe(this);
    this.onVis = () => { if (document.hidden) this.stop(true); else if (this.visible && !this.userPaused) this.start(); };
    document.addEventListener('visibilitychange', this.onVis);
    this.state('paused');
  }

  disconnectedCallback() {
    this.io?.disconnect();
    if (this.onVis) document.removeEventListener('visibilitychange', this.onVis);
    clearTimeout(this.timer);
  }

  /** Slides 2–n live in <template>s until the loop first runs (nothing is fetched under reduced motion). */
  slides() {
    if (!this.unpacked) {
      this.querySelectorAll('template[data-slide-template]').forEach((t) => t.replaceWith(t.content.cloneNode(true)));
      this.unpacked = true;
    }
    return [...this.querySelectorAll('[data-slide]')];
  }

  state(s) {
    this.dataset.state = s;
    if (this.btn) this.btn.setAttribute('aria-label', s === 'playing' ? this.dataset.labelPause : this.dataset.labelPlay);
  }

  start() {
    if (this.dataset.state === 'playing') return;
    this.slides();
    this.state('playing');
    this.schedule();
  }

  stop(auto) {
    clearTimeout(this.timer);
    if (this.dataset.state === 'playing' || !auto) this.state('paused');
  }

  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.advance(), this.still + this.fade);
  }

  advance() {
    if (this.dataset.state !== 'playing') return;
    const all = this.slides();
    if (all.length < 2) return;
    const from = all[this.index];
    this.index = (this.index + 1) % all.length;
    const to = all[this.index];
    to.classList.add('is-active');
    to.removeAttribute('aria-hidden');
    from.classList.remove('is-active');
    from.setAttribute('aria-hidden', 'true');
    this.schedule();
  }
}

customElements.get('nono-slideshow') || customElements.define('nono-slideshow', NonoSlideshow);

// ---- §01 hero gallery (owner 19.10.2026, Figma 1679:8023) ----------------------------------------------------
// Thumbnails under the slide and a full-screen view. Both are copies of the slide itself: every size inside a slide
// is in «k» (slide width / 350, container units), so a copy in a 56 px box is a sharp miniature and a copy in a
// 720 px box is the slide redrawn at full size (zoom = a 2× wider box, still sharp text). The «n av 10» counter,
// arrows, ← → and swipe move through the slides; Esc closes; the carousel follows to the slide last shown.

const settled = (fn) => {
  const idle = () => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 300));
  if (document.readyState === 'complete') idle(); else addEventListener('load', idle, { once: true });
};

const strip = (node, keepFilm) => {
  node.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
  node.querySelectorAll('[data-booking-trigger], a, button').forEach((el) => {
    if (!keepFilm || !el.closest('nono-loop')) el.setAttribute('tabindex', '-1');
  });
  if (!keepFilm) {
    node.querySelectorAll('nono-loop').forEach((film) => {
      const poster = film.querySelector('.c-film__poster');
      if (poster) film.replaceWith(poster); else film.remove();
    });
  }
  node.querySelectorAll('img').forEach((img) => {
    img.loading = 'lazy';
    if (!keepFilm) {
      // Thumbnail: one small file (Shopify CDN width=160) instead of the slide's ladder (speed 21.10.2026).
      const first = (img.dataset.srcset || img.getAttribute('srcset') || '').split(',')[0].trim().split(' ')[0] || img.dataset.src || img.getAttribute('src') || '';
      const small = first.replace(/([?&])width=\d+/, '$1width=160');
      node.querySelectorAll('source').forEach((s) => s.remove());
      ['srcset', 'sizes', 'data-srcset', 'data-sizes', 'data-src', 'data-defer'].forEach((k) => img.removeAttribute(k));
      if (small) img.dataset.thumb = small; // fetched in gallery() once the page has settled (speed 03.11.2026)
    }
  });
  if (keepFilm) window.nonoHydrate?.(node);
  return node;
};

function gallery(section) {
  const carousel = section.querySelector('nono-carousel');
  const track = carousel?.querySelector('[data-carousel-track]');
  const row = carousel?.querySelector('[data-ptc-thumbs]');
  const dialog = section.querySelector('[data-ptc-lightbox]');
  if (!track || !row || !dialog) return;
  const slides = [...track.children];
  const n = slides.length;
  const of = dialog.dataset.of || 'av';
  const label = (i) => slides[i].getAttribute('aria-label') || `${i + 1} ${of} ${n}`;
  const thumbText = row.dataset.thumbLabel || 'Vis lysbilde';
  const current = () => {
    const box = track.getBoundingClientRect();
    return Math.max(0, slides.findIndex((s) => Math.abs(s.getBoundingClientRect().left - box.left) < s.offsetWidth / 2));
  };

  // Thumbnails
  const thumbs = slides.map((slide, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'p-ptc__thumb';
    b.setAttribute('aria-label', `${thumbText} ${label(i)}`);
    const mini = document.createElement('span');
    mini.className = 'p-ptc__thumb-mini';
    mini.setAttribute('aria-hidden', 'true');
    mini.inert = true;
    const frame = slide.querySelector('.p-ptc__frame');
    if (frame) mini.append(strip(frame.cloneNode(true), false));
    if (slide.classList.contains('p-ptc__slide--video')) b.classList.add('p-ptc__thumb--video');
    b.append(mini);
    b.addEventListener('click', () => carousel.to(i));
    return b;
  });
  row.replaceChildren(...thumbs);
  // The thumbnails are below the hero poster: fetch them once the page has settled, or on the first touch.
  const minis = () => row.querySelectorAll('img[data-thumb]').forEach((img) => {
    img.src = img.dataset.thumb;
    img.removeAttribute('data-thumb');
  });
  settled(minis);
  ['pointerdown', 'focusin'].forEach((t) => row.addEventListener(t, minis, { once: true, passive: true }));
  track.addEventListener('scroll', minis, { once: true, passive: true });
  const mark = () => {
    const c = current();
    thumbs.forEach((t, i) => t.toggleAttribute('aria-current', i === c));
  };
  track.addEventListener('scroll', () => requestAnimationFrame(mark), { passive: true });
  mark();

  // Full-screen view
  const css = dialog.dataset.css;
  if (css && !document.querySelector(`link[href="${css}"]`)) {
    document.head.append(Object.assign(document.createElement('link'), { rel: 'stylesheet', href: css }));
  }
  const view = dialog.querySelector('[data-lb-view]');
  const stage = dialog.querySelector('[data-lb-stage]');
  const count = dialog.querySelector('[data-lb-count]');
  const cap = dialog.querySelector('[data-lb-caption]');
  let at = 0;
  const zoom = (on, e) => {
    const was = dialog.hasAttribute('data-zoomed');
    if (on === was) return;
    dialog.toggleAttribute('data-zoomed', on);
    stage.setAttribute('aria-label', on ? dialog.dataset.labelZoomOut : dialog.dataset.labelZoomIn);
    if (on && e) {
      // Keep the clicked point under the pointer.
      const r = view.getBoundingClientRect();
      const fx = (e.clientX - r.left) / r.width;
      const fy = (e.clientY - r.top) / r.height;
      requestAnimationFrame(() => {
        view.scrollLeft = fx * view.scrollWidth - (e.clientX - r.left);
        view.scrollTop = fy * view.scrollHeight - (e.clientY - r.top);
      });
    }
  };
  const show = (i) => {
    at = (i + n) % n;
    zoom(false);
    const frame = slides[at].querySelector('.p-ptc__frame');
    stage.replaceChildren(frame ? strip(frame.cloneNode(true), true) : '');
    const c = slides[at].querySelector('.p-ptc__caption');
    cap.replaceChildren(...(c ? [strip(c.cloneNode(true), true)] : []));
    count.textContent = `${at + 1} ${of} ${n}`;
  };
  const open = (i, opener) => {
    dialog._opener = opener;
    show(i);
    dialog.showModal();
  };
  slides.forEach((slide, i) => {
    const frame = slide.querySelector('.p-ptc__frame');
    if (!frame) return;
    frame.classList.add('p-ptc__frame--open');
    frame.addEventListener('click', (e) => {
      // Buttons and links inside a slide (booking, film pause) keep their own job.
      if (e.target instanceof Element && e.target.closest('a, button')) return;
      open(i, slide);
    });
  });
  const expand = carousel.querySelector('[data-ptc-open]');
  if (expand) {
    expand.hidden = false;
    expand.addEventListener('click', () => open(current(), expand));
  }
  stage.setAttribute('role', 'button');
  stage.tabIndex = 0;
  stage.setAttribute('aria-label', dialog.dataset.labelZoomIn);
  stage.addEventListener('click', (e) => {
    if (e.target instanceof Element && e.target.closest('a, button')) return;
    if (matchMedia('(pointer: fine)').matches) zoom(!dialog.hasAttribute('data-zoomed'), e);
  });
  stage.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); zoom(!dialog.hasAttribute('data-zoomed')); } });
  dialog.querySelector('[data-lb-close]').addEventListener('click', () => dialog.close());
  dialog.querySelector('[data-lb-prev]').addEventListener('click', () => show(at - 1));
  dialog.querySelector('[data-lb-next]').addEventListener('click', () => show(at + 1));
  dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
  dialog.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); show(at + 1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); show(at - 1); }
  });
  let x0 = null;
  view.addEventListener('touchstart', (e) => { x0 = e.touches.length === 1 ? e.touches[0].clientX : null; }, { passive: true });
  view.addEventListener('touchend', (e) => {
    if (x0 === null || dialog.hasAttribute('data-zoomed') || (window.visualViewport && visualViewport.scale > 1.05)) return;
    const dx = e.changedTouches[0].clientX - x0;
    x0 = null;
    if (Math.abs(dx) > 50) show(at + (dx < 0 ? 1 : -1));
  });
  dialog.addEventListener('close', () => {
    stage.replaceChildren();
    carousel.to(at);
    (dialog._opener === expand ? expand : thumbs[at])?.focus({ preventScroll: true });
  });
}

document.querySelectorAll('.p-ptc').forEach(gallery);
